import {
  ArrowUpRight,
  BookOpen,
  Building2,
  Check,
  Clock,
  Copy,
  Download,
  ExternalLink,
  FileText,
  Hash,
  Layers,
  Mail,
  ShieldAlert,
  User,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { ClientSelect } from "@/components/client-select";
import { ProjectResourcesPanel } from "@/components/project-resources-panel";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  addProjectMember,
  getProjectLogs,
  getProjectSentry,
  getProjectVibe,
  isApiError,
  listProjectMembers,
  type ProjectMember,
  type ProjectSentryStatus,
  type ProjectSummary,
  type ProjectVibe,
  removeProjectMember,
  setupProjectSentry,
  useAdminSetProjectClient,
  useClients,
  type UserSearchResult,
} from "@/lib/api";
import { formatDate } from "@/lib/format";
import type { ProjectLogsResponse } from "@/lib/local-types";
import { logger } from "@/lib/logger";
import { getEffectiveStatus } from "@/lib/project-status";
import { useUser } from "@/lib/user-context";

import { InstructionsDialog } from "./instructions-dialog";
import { InviteMemberDialog } from "./invite-member-dialog";
import { MemberSearchCombobox } from "./member-search-combobox";
import { ScaffoldProgress, StepLogs } from "./scaffold-progress";

const BASE_STEPS = [
  { key: "creating_repo", label: "Creating GitHub repository" },
  { key: "scaffolding", label: "Running scaffold template" },
  { key: "preparing", label: "Installing dependencies" },
] as const;

const PERSONALIZE_STEP = { key: "personalizing", label: "Personalizing project" } as const;
const SUPABASE_STEP = { key: "setting_up_supabase", label: "Setting up Supabase" } as const;
const PUSH_STEP = { key: "pushing", label: "Pushing to GitHub" } as const;
const RAILWAY_STEP = { key: "setting_up_railway", label: "Deploying to Railway" } as const;
const INSTRUCTIONS_STEP = {
  key: "generating_instructions",
  label: "Creating setup instructions",
} as const;

type Step = { readonly key: string; readonly label: string };

function buildSteps(
  includePersonalization: boolean,
  includeSupabase: boolean,
  includeRailway: boolean,
): Step[] {
  const steps: Step[] = [...BASE_STEPS];
  if (includePersonalization) {
    steps.push(PERSONALIZE_STEP);
  }
  if (includeSupabase) {
    steps.push(SUPABASE_STEP);
  }
  steps.push(PUSH_STEP);
  if (includeRailway) {
    steps.push(RAILWAY_STEP);
  }
  steps.push(INSTRUCTIONS_STEP);
  return steps;
}

const IN_PROGRESS_STATUSES = new Set([
  "pending",
  "creating_repo",
  "scaffolding",
  "preparing",
  "personalizing",
  "setting_up_supabase",
  "pushing",
  "setting_up_railway",
  "generating_instructions",
]);

interface ProjectDetailDialogProps {
  project: ProjectSummary | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onProjectDeleted?: () => void;
  onResourceUpdated?: (projectId: string | number, updates: Partial<ProjectSummary>) => void;
  tab?: string;
  onTabChange?: (tab: string) => void;
}

export function ProjectDetailDialog({
  project,
  open,
  onOpenChange,
  onProjectDeleted,
  onResourceUpdated,
  tab,
  onTabChange,
}: ProjectDetailDialogProps) {
  const { user } = useUser();
  const [localProject, setLocalProject] = useState<ProjectSummary | null>(project);
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersFetchError, setMembersFetchError] = useState<string | null>(null);
  const [membersAccessDenied, setMembersAccessDenied] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteInitialEmail, setInviteInitialEmail] = useState("");
  const [addingMember, setAddingMember] = useState(false);
  const [memberError, setMemberError] = useState("");
  const ownerByProject = !!user?.email && project?.owner_email === user.email;
  const ownerByMembers = members.some((m) => m.role === "owner" && m.user_id === user?.id);
  const isProjectOwner = ownerByProject || ownerByMembers;
  const isAdmin = user?.role === "admin";
  const canManageResources = isProjectOwner || isAdmin;
  const [downloading, setDownloading] = useState(false);
  const [instructionsOpen, setInstructionsOpen] = useState(false);
  const [clientError, setClientError] = useState("");
  const { data: clients = [] } = useClients();
  const adminSetProjectClientMutation = useAdminSetProjectClient();
  const activeTab = tab ?? "details";
  const handleTabChange = useCallback(
    (newTab: string) => {
      onTabChange?.(newTab);
    },
    [onTabChange],
  );

  useEffect(() => {
    // Mirror the parent's project prop into local state so optimistic updates
    // (member add/remove, name edits) can mutate without affecting the parent
    // until the next refresh. Sync on prop change.

    setLocalProject(project);
  }, [project]);

  useEffect(() => {
    if (project?.status !== "completed" && activeTab === "mascot") {
      onTabChange?.("details");
    }
  }, [project?.status, activeTab, onTabChange]);

  const fetchMembers = useCallback(async () => {
    if (!project) {
      return;
    }
    setMembersLoading(true);
    setMembersFetchError(null);
    setMembersAccessDenied(false);
    try {
      const { data } = await listProjectMembers({ path: { project_id: project.id } });
      setMembers(data ?? []);
    } catch (err) {
      logger.error("[project-detail] failed to fetch members:", err);
      const status = isApiError(err) ? err.status : undefined;
      if (status === 403 || status === 404) {
        setMembersAccessDenied(true);
        setMembersFetchError("You don't have access to this project's members.");
      } else {
        setMembersFetchError("Failed to load members. Please try again.");
      }
    } finally {
      setMembersLoading(false);
    }
  }, [project]);

  useEffect(() => {
    if (!open || !project) {
      // Reset member list state when dialog closes. setState in cleanup
      // would be cleaner but the next effect run also needs the cleared
      // state, so we keep it inline with a targeted disable.

      setMembers([]);

      setMemberError("");

      setMembersFetchError(null);

      setMembersAccessDenied(false);
      return;
    }

    fetchMembers();
  }, [open, project, fetchMembers]);

  const handleAddMember = async (selectedUser: UserSearchResult) => {
    if (!project) {
      return;
    }
    setAddingMember(true);
    setMemberError("");

    try {
      await addProjectMember({
        path: { project_id: project.id },
        body: { email: selectedUser.email },
      });
      await fetchMembers();
    } catch (err: unknown) {
      const detail = isApiError(err)
        ? (err.body as { detail?: string } | undefined)?.detail
        : undefined;
      if (detail === "User is already a member") {
        setMemberError("User is already a member.");
      } else {
        setMemberError("Failed to add member.");
      }
      logger.error("[project-detail] add member failed:", err);
    } finally {
      setAddingMember(false);
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!project) {
      return;
    }
    try {
      await removeProjectMember({
        path: { project_id: project.id, member_user_id: memberId },
      });
      await fetchMembers();
    } catch (err) {
      logger.error("[project-detail] remove member failed:", err);
    }
  };

  const handleDownloadEnv = async () => {
    if (!project) {
      return;
    }
    setDownloading(true);
    try {
      // Route handler names the file "{slug}.env" because Chrome strips
      // leading dots from download filenames.
      // Use a temporary anchor instead of window.location.href so that
      // non-OK responses (401, 5xx) don't navigate the user away from the app.
      const url = `/api/projects/${project.id}/env?slug=${encodeURIComponent(p.project_slug)}`;
      const resp = await fetch(url);
      if (!resp.ok) {
        const detail = await resp.text().catch(() => "");
        logger.error("[project-detail] env download failed:", resp.status, detail);
        return;
      }
      const blob = await resp.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `${p.project_slug}.env`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      logger.error("[project-detail] env download failed:", err);
    } finally {
      setDownloading(false);
    }
  };

  const handleClientChange = async (clientId: number | null) => {
    if (!project) {
      return;
    }
    setClientError("");
    const clientName =
      clientId === null ? null : (clients.find((c) => c.id === clientId)?.name ?? null);
    // Optimistically patch local + parent state so the change reflects
    // immediately, mirroring the resource-deletion pattern below.
    setLocalProject((prev) =>
      prev ? { ...prev, client_id: clientId, client_name: clientName } : prev,
    );
    onResourceUpdated?.(project.id, { client_id: clientId, client_name: clientName });
    try {
      // Reassigning a project's client is admin-only — the Client row only
      // renders the editable ClientSelect for admins (see isAdmin above).
      await adminSetProjectClientMutation.mutateAsync({ projectId: project.id, clientId });
    } catch (err) {
      logger.error("[project-detail] set client failed:", err);
      setClientError("Failed to update client. Please try again.");
      // Revert to the last known-good value from the parent's project prop.
      setLocalProject((prev) =>
        prev ? { ...prev, client_id: project.client_id, client_name: project.client_name } : prev,
      );
      onResourceUpdated?.(project.id, {
        client_id: project.client_id,
        client_name: project.client_name,
      });
    }
  };

  if (!project) {
    return null;
  }

  const p = localProject ?? project;

  const status = getEffectiveStatus(p);

  // BOIE-177: a copy-pastable prompt the user drops into Claude (Desktop) to
  // clone the new repo and run first-time setup, instead of following the
  // manual steps by hand. Supabase projects also need the env file downloaded
  // from this dialog (named "<slug>.env" because browsers strip leading dots)
  // moved into place before installing.
  const envStep = p.include_supabase
    ? `move \`~/Downloads/${p.project_slug}.env\` into the project root and rename it to \`.env\`, `
    : "";
  const setupPrompt = p.github_url
    ? `Clone my new project from ${p.github_url}, then set it up for me: ${envStep}run \`make prepare\` to install everything and \`make dev\` to start it, and tell me the URL to open in my browser.`
    : "";

  async function copySetupPrompt() {
    if (!setupPrompt) {
      return;
    }
    try {
      await navigator.clipboard.writeText(setupPrompt);
      toast.success("Prompt copied. Paste it into Claude to set up your project.");
    } catch (err) {
      // Denied permission or an insecure context: the prompt is the whole
      // point of the card, so say so instead of claiming a copy that failed.
      logger.error("[project-detail] copy setup prompt failed:", err);
      toast.error("Could not copy the prompt. Select it below and copy manually.");
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-3xl" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader className="sr-only">
            <DialogTitle>{p.project_name}</DialogTitle>
            <DialogDescription>Project details</DialogDescription>
          </DialogHeader>

          <DialogBody className="pt-6">
            <div className="space-y-5">
              {/* Header */}
              <div className="flex items-start justify-between gap-3 pr-6">
                <div className="flex min-w-0 flex-1 items-start gap-3">
                  {p.app_url && (
                    <a
                      href={p.app_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-primary text-primary-foreground mt-0.5 inline-flex shrink-0 items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium shadow-sm transition-opacity hover:opacity-90"
                    >
                      Open App <ArrowUpRight className="size-3.5" />
                    </a>
                  )}
                  <div className="min-w-0">
                    <h3 className="text-foreground truncate text-base font-bold">
                      {p.project_name}
                    </h3>
                    <p className="text-muted truncate text-sm">{p.project_slug}</p>
                  </div>
                </div>
                <Badge variant={status.variant}>{status.label}</Badge>
              </div>

              {/* Tabs */}
              <Tabs value={activeTab} onValueChange={handleTabChange}>
                <TabsList variant="line" className="w-full justify-start">
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="members">Members</TabsTrigger>
                  <TabsTrigger value="logs">Logs</TabsTrigger>
                  <TabsTrigger value="resources">Resources</TabsTrigger>
                  {project.status === "completed" && (
                    <TabsTrigger value="mascot">Mascot</TabsTrigger>
                  )}
                </TabsList>

                {/* Details Tab */}
                <TabsContent
                  value="details"
                  forceMount
                  className="space-y-4 pt-4 data-[state=inactive]:hidden"
                >
                  <div className="space-y-3">
                    <DetailRow icon={User} label="Owner">
                      {p.owner_name || p.owner_email || "Unknown"}
                    </DetailRow>
                    <DetailRow icon={Building2} label="Client">
                      {isAdmin ? (
                        <div className="flex items-center gap-2">
                          <ClientSelect
                            value={p.client_id ?? null}
                            onChange={handleClientChange}
                            disabled={adminSetProjectClientMutation.isPending}
                            className="h-7"
                          />
                          {clientError && (
                            <span className="text-destructive text-xs">{clientError}</span>
                          )}
                        </div>
                      ) : (
                        p.client_name || "Unassigned"
                      )}
                    </DetailRow>
                    <DetailRow icon={FileText} label="Name">
                      {p.project_name}
                    </DetailRow>
                    <DetailRow icon={Hash} label="Slug">
                      {p.project_slug}
                    </DetailRow>
                    <DetailRow icon={FileText} label="Desc.">
                      <span className="line-clamp-3">{p.description}</span>
                    </DetailRow>
                    <DetailRow icon={Hash} label="Python">
                      {p.python_version}
                    </DetailRow>
                    {/* Read from the rendered template; empty until it has rendered. */}
                    {p.node_version && (
                      <DetailRow icon={Hash} label="Node">
                        {p.node_version}
                      </DetailRow>
                    )}
                    {p.template_version && (
                      <DetailRow icon={Layers} label="Template">
                        <span className="bg-secondary text-secondary-foreground rounded-md px-1.5 py-0.5 text-xs">
                          BOI Vibe Template {p.template_version}
                        </span>
                      </DetailRow>
                    )}
                    {p.include_supabase && p.allowed_email_domains && (
                      <DetailRow icon={Mail} label="Domains">
                        <div className="flex flex-wrap gap-1">
                          {p.allowed_email_domains.split(",").map((domain) => (
                            <span
                              key={domain}
                              className="bg-secondary text-secondary-foreground rounded-md px-1.5 py-0.5 text-xs"
                            >
                              {domain}
                            </span>
                          ))}
                        </div>
                      </DetailRow>
                    )}
                    {p.email_sender_address && (
                      <DetailRow icon={Mail} label="Sender">
                        {p.email_sender_address}
                      </DetailRow>
                    )}
                    {p.error_message && (
                      <DetailRow icon={X} label="Error">
                        <span className="text-destructive">{p.error_message}</span>
                      </DetailRow>
                    )}
                    {p.railway_error_message && (
                      <DetailRow icon={X} label="Railway">
                        <span className="text-amber-600 dark:text-amber-400">
                          {p.railway_error_message}
                        </span>
                      </DetailRow>
                    )}
                    <DetailRow icon={Clock} label="Date">
                      {formatDate(p.created_at)}
                    </DetailRow>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap items-center gap-2">
                    {p.github_url && (
                      <a href={p.github_url} target="_blank" rel="noopener noreferrer">
                        <Button size="sm" variant="outline">
                          <ExternalLink className="mr-2 size-4" />
                          Open in GitHub
                        </Button>
                      </a>
                    )}
                    {p.supabase_dashboard_url && (
                      <a href={p.supabase_dashboard_url} target="_blank" rel="noopener noreferrer">
                        <Button size="sm" variant="outline">
                          <ExternalLink className="mr-2 size-4" />
                          Open Supabase
                        </Button>
                      </a>
                    )}
                    {p.railway_dashboard_url && (
                      <a href={p.railway_dashboard_url} target="_blank" rel="noopener noreferrer">
                        <Button size="sm" variant="outline">
                          <ExternalLink className="mr-2 size-4" />
                          Open Railway
                        </Button>
                      </a>
                    )}
                  </div>

                  {p.status === "completed" && p.github_url && (
                    <div className="space-y-2 rounded-lg border p-3">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-medium">Set up in Claude</p>
                        <Button size="sm" variant="outline" onClick={copySetupPrompt}>
                          <Copy className="mr-2 size-3.5" />
                          Copy prompt
                        </Button>
                      </div>
                      <p className="text-muted text-xs">
                        Paste this into Claude to clone and start your project.
                      </p>
                      <pre className="bg-primary/5 overflow-x-auto rounded border p-2 font-mono text-xs whitespace-pre-wrap">
                        {setupPrompt}
                      </pre>
                    </div>
                  )}

                  {p.status === "completed" && (
                    <SentryCard key={p.id} project={p} canManage={canManageResources} />
                  )}

                  {p.status === "completed" && (
                    <div className="flex flex-wrap items-center gap-2 border-t pt-3">
                      {p.include_supabase && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={handleDownloadEnv}
                                disabled={downloading}
                              >
                                <Download className="mr-2 size-4" />
                                {downloading ? "Downloading..." : "Download .env"}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>
                                Place in your project root and rename to{" "}
                                <code className="font-mono">.env</code>
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => setInstructionsOpen(true)}>
                        <BookOpen className="mr-2 size-4" />
                        How to get started
                      </Button>
                    </div>
                  )}
                </TabsContent>

                {/* Members Tab */}
                <TabsContent
                  value="members"
                  forceMount
                  className="space-y-4 pt-4 data-[state=inactive]:hidden"
                >
                  {membersLoading ? (
                    <p className="text-muted py-8 text-center text-sm">Loading...</p>
                  ) : membersFetchError ? (
                    <p className="text-muted py-8 text-center text-sm">{membersFetchError}</p>
                  ) : (
                    <>
                      {members.length === 0 ? (
                        <p className="text-muted py-8 text-center text-sm">No members found.</p>
                      ) : (
                        <div className="max-h-60 space-y-1 overflow-y-auto">
                          {members.map((member) => (
                            <div
                              key={member.user_id}
                              className="flex items-center justify-between rounded-md px-2 py-2"
                            >
                              <div className="min-w-0 flex-1">
                                <p className="text-foreground truncate text-sm font-medium">
                                  {member.full_name || member.email.split("@")[0]}
                                </p>
                                <p className="text-muted truncate text-xs">{member.email}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge variant={member.role === "owner" ? "default" : "secondary"}>
                                  {member.role}
                                </Badge>
                                {canManageResources && member.role !== "owner" && (
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="size-7"
                                    onClick={() => handleRemoveMember(member.user_id)}
                                  >
                                    <X className="size-3.5" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {members.filter((m) => m.role !== "owner").length === 0 &&
                        members.length > 0 && (
                          <p className="text-muted text-center text-xs">No additional members</p>
                        )}

                      {canManageResources && (
                        <div className="space-y-2">
                          <MemberSearchCombobox
                            excludeIds={members.map((m) => m.user_id)}
                            onSelect={handleAddMember}
                            onInviteClick={(currentQuery) => {
                              setInviteInitialEmail(currentQuery);
                              setInviteDialogOpen(true);
                            }}
                            disabled={addingMember}
                          />
                          {memberError && <p className="text-destructive text-xs">{memberError}</p>}
                        </div>
                      )}
                    </>
                  )}
                </TabsContent>

                {/* Logs Tab */}
                <TabsContent value="logs" forceMount className="pt-4 data-[state=inactive]:hidden">
                  {activeTab === "logs" && (
                    <LogsContent
                      project={p}
                      hasAccess={
                        !membersAccessDenied &&
                        (isProjectOwner ||
                          user?.role === "admin" ||
                          members.some((m) => m.user_id === user?.id))
                      }
                      membersLoading={membersLoading}
                      membersLoadError={!membersAccessDenied && !!membersFetchError}
                    />
                  )}
                </TabsContent>

                {/* Resources Tab */}
                <TabsContent
                  value="resources"
                  forceMount
                  className="pt-4 data-[state=inactive]:hidden"
                >
                  {activeTab === "resources" && (
                    <ProjectResourcesPanel
                      project={p}
                      canManage={canManageResources}
                      onResourceDeleted={(updates) => {
                        setLocalProject((prev) => (prev ? { ...prev, ...updates } : prev));
                        if (project) {
                          onResourceUpdated?.(project.id, updates);
                        }
                      }}
                      onProjectDeleted={() => {
                        onOpenChange(false);
                        onProjectDeleted?.();
                      }}
                    />
                  )}
                </TabsContent>

                {/* Mascot Tab */}
                {project.status === "completed" && (
                  <TabsContent
                    value="mascot"
                    forceMount
                    className="pt-4 data-[state=inactive]:hidden"
                  >
                    {activeTab === "mascot" && <MascotContent projectId={project.id} />}
                  </TabsContent>
                )}
              </Tabs>
            </div>
          </DialogBody>
        </DialogContent>
      </Dialog>
      <InviteMemberDialog
        open={inviteDialogOpen}
        onOpenChange={setInviteDialogOpen}
        initialEmail={inviteInitialEmail}
      />
      <InstructionsDialog
        projectId={project.id}
        open={instructionsOpen}
        onOpenChange={setInstructionsOpen}
      />
    </>
  );
}

/** The prompt that asks Claude to add the Sentry SDK to the project's own code. */
function buildSentryPrompt(project: ProjectSummary, status: ProjectSentryStatus | null): string {
  const source =
    status?.backend_dsn && status.frontend_dsn
      ? `Use these DSNs:\nbackend: ${status.backend_dsn}\nfrontend: ${status.frontend_dsn}`
      : `First create two projects in the board-of-innovation Sentry org (EU region, https://de.sentry.io): ` +
        `${project.project_slug}-backend (platform python-fastapi) and ${project.project_slug}-frontend ` +
        `(platform javascript-react), and take their DSNs.`;

  return [
    "Add Sentry error monitoring to this project.",
    source,
    "Then install and initialise the Sentry SDKs in both halves (FastAPI backend, React frontend), " +
      "read the DSNs from SENTRY_DSN and VITE_SENTRY_DSN, add both to .env.example, tag events with " +
      "the environment, and declare `ARG VITE_SENTRY_DSN` in the frontend Dockerfile's builder stage " +
      "before the build step so the deploy actually injects it.",
  ].join("\n\n");
}

/** Sentry card -- provisions this project's Sentry projects on demand (BOIE-178). */
function SentryCard({ project, canManage }: { project: ProjectSummary; canManage: boolean }) {
  const [status, setStatus] = useState<ProjectSentryStatus | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Once setup has been asked for, its result wins: the status read started
  // earlier and would otherwise land later with a pre-provisioning snapshot.
  const setupStarted = useRef(false);

  const prompt = buildSentryPrompt(project, status);
  // Provisioning is owner-or-admin only server-side, so a plain member never
  // sees the button. Until the status read lands, assume it is worth offering:
  // hiding it and then popping it in reads worse than a click that reports 503.
  const canProvision = canManage && (status === null || status.server_configured);

  // Read the existing state once, so reopening the dialog shows whether Sentry
  // is already wired instead of making the user re-run provisioning to find out.
  useEffect(() => {
    if (!canManage) {
      // The status route is owner-only too: don't fire a request that can only 403.
      return;
    }

    let cancelled = false;

    getProjectSentry({ path: { project_id: project.id } })
      .then(({ data }) => {
        if (!cancelled && !setupStarted.current) {
          setStatus(data ?? null);
        }
      })
      .catch((err) => {
        // A failed read is not worth surfacing: the prompt below still works.
        logger.debug("[project-detail] sentry status unavailable:", err);
      });

    return () => {
      cancelled = true;
    };
  }, [project.id, canManage]);

  const handleSetup = async () => {
    setupStarted.current = true;
    setPending(true);
    setError(null);
    try {
      const { data } = await setupProjectSentry({ path: { project_id: project.id } });
      setStatus(data ?? null);
    } catch (err) {
      // A failed setup has no result to protect, so let a late status read
      // speak again -- otherwise the card would keep offering the button that
      // just failed.
      setupStarted.current = false;
      logger.error("[project-detail] sentry setup failed:", err);
      const detail = isApiError(err)
        ? (err.body as { detail?: string } | undefined)?.detail
        : undefined;
      // A 503 here means no SENTRY_AUTH_TOKEN on the server: the prompt below
      // still works, it just has to create the Sentry projects itself.
      setError(detail ?? "Could not reach Sentry. Use the prompt below instead.");
    } finally {
      setPending(false);
    }
  };

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      toast.success("Prompt copied. Paste it into Claude to wire up Sentry.");
    } catch (err) {
      logger.error("[project-detail] copy sentry prompt failed:", err);
      toast.error("Could not copy the prompt. Select it below and copy manually.");
    }
  };

  return (
    <div className="space-y-2 rounded-lg border p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium">Sentry monitoring</p>
        <div className="flex items-center gap-2">
          {canProvision && (
            <Button size="sm" variant="outline" onClick={handleSetup} disabled={pending}>
              <ShieldAlert className="mr-2 size-3.5" />
              {pending ? "Setting up..." : status?.provisioned ? "Re-run setup" : "Set up Sentry"}
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={copyPrompt}>
            <Copy className="mr-2 size-3.5" />
            Copy prompt
          </Button>
        </div>
      </div>
      {/* While an error is shown it is the card's statement about its own state:
          a stale availability line next to it would contradict it. */}
      {!error && (
        <p className="text-muted text-xs">
          {!canManage
            ? "Automatic setup is owner-only, but the prompt below works for anyone with the repo."
            : canProvision
              ? "Creates this project's Sentry projects, then hand the prompt to Claude to add the SDK to the code."
              : "Automatic setup is not configured on this server, so the prompt asks Claude to create the Sentry projects too."}
        </p>
      )}

      {error && <p className="text-destructive text-xs">{error}</p>}

      {status?.provisioned && (
        <div className="space-y-1 text-xs">
          <p className="text-green-700 dark:text-green-400">
            Sentry projects ready.{" "}
            {status.railway_vars_set === true && "DSNs set as Railway service variables."}
            {status.railway_vars_set === false && "DSNs are in the prompt below."}
          </p>
          {status.railway_error && (
            <p className="text-amber-600 dark:text-amber-400">{status.railway_error}</p>
          )}
          <div className="flex flex-wrap gap-3">
            {status.backend_dashboard_url && (
              <a
                href={status.backend_dashboard_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent inline-flex items-center gap-1 underline underline-offset-2"
              >
                Backend project <ExternalLink className="size-3" />
              </a>
            )}
            {status.frontend_dashboard_url && (
              <a
                href={status.frontend_dashboard_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent inline-flex items-center gap-1 underline underline-offset-2"
              >
                Frontend project <ExternalLink className="size-3" />
              </a>
            )}
          </div>
        </div>
      )}

      <pre className="bg-primary/5 overflow-x-auto rounded border p-2 font-mono text-xs whitespace-pre-wrap">
        {prompt}
      </pre>
    </div>
  );
}

/** Logs tab content -- live SSE for in-progress, static summary for terminal. */
function LogsContent({
  project,
  hasAccess,
  membersLoading,
  membersLoadError,
}: {
  project: ProjectSummary;
  hasAccess: boolean;
  membersLoading: boolean;
  membersLoadError: boolean;
}) {
  if (membersLoading) {
    return <p className="text-muted py-8 text-center text-sm">Loading...</p>;
  }

  if (membersLoadError) {
    return (
      <p className="text-muted py-8 text-center text-sm">
        Could not verify access. Failed to load member information. Please try again later.
      </p>
    );
  }

  if (!hasAccess) {
    return (
      <p className="text-muted py-8 text-center text-sm">
        You don&apos;t have access to logs for this project. Only project owners and members can
        view logs.
      </p>
    );
  }

  if (IN_PROGRESS_STATUSES.has(project.status)) {
    return (
      <ScaffoldProgress
        projectId={project.id}
        includeSupabase={project.include_supabase}
        includeRailway={project.include_railway}
        onComplete={() => {
          // The dialog will re-render with updated status on next fetch
        }}
        onFailed={() => {
          // The dialog will re-render with updated status on next fetch
        }}
      />
    );
  }

  return <HistoricalLogs project={project} />;
}

/** Fetches persisted build logs and renders them with collapsible terminals per step. */
function HistoricalLogs({ project }: { project: ProjectSummary }) {
  const [buildLog, setBuildLog] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());

  useEffect(() => {
    getProjectLogs({ path: { project_id: project.id } })
      .then(({ data }) => setBuildLog((data as ProjectLogsResponse | undefined)?.build_log ?? null))
      .catch(() => setBuildLog(null))
      .finally(() => setLoading(false));
  }, [project.id]);

  const logsByStep = useMemo(() => {
    if (!buildLog) {
      return {};
    }
    const result: Record<string, string[]> = {};
    for (const line of buildLog.split("\n")) {
      const match = line.match(/^\[([^\]]+)\]\s(.*)$/);
      if (match) {
        const step = match[1];
        const text = match[2];
        if (step !== undefined) {
          (result[step] ??= []).push(text ?? "");
        }
      }
    }
    return result;
  }, [buildLog]);

  const showPersonalizeStep = !!logsByStep["personalizing"];
  const showSupabaseStep = project.include_supabase || project.status === "setting_up_supabase";
  const showRailwayStep = project.include_railway || project.status === "setting_up_railway";
  const steps = buildSteps(showPersonalizeStep, showSupabaseStep, showRailwayStep);
  const railwayFailed = project.include_railway && !!project.railway_error_message;

  if (loading) {
    return <p className="text-muted py-8 text-center text-sm">Loading logs...</p>;
  }

  function toggleStep(key: string) {
    setExpandedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  return (
    <div className="space-y-1">
      {steps.map((step) => {
        const isRailwayFailedStep = step.key === "setting_up_railway" && railwayFailed;
        const isFailed = project.status === "failed";
        const isCancelled = project.status === "cancelled";
        const stepLines = logsByStep[step.key] ?? [];

        return (
          <div key={step.key}>
            <div className="flex items-center gap-3 py-1.5">
              <div className="flex size-6 shrink-0 items-center justify-center">
                {isFailed || isCancelled ? (
                  <div className="bg-muted size-2 rounded-full" />
                ) : isRailwayFailedStep ? (
                  <X className="size-5 text-amber-600 dark:text-amber-400" />
                ) : (
                  <Check className="size-5 text-green-600 dark:text-green-400" />
                )}
              </div>
              <span
                className={
                  isFailed || isCancelled
                    ? "text-muted-foreground text-sm"
                    : isRailwayFailedStep
                      ? "text-sm text-amber-700 dark:text-amber-400"
                      : "text-sm text-green-700 dark:text-green-400"
                }
              >
                {isRailwayFailedStep ? `${step.label} (failed)` : step.label}
              </span>
            </div>
            {stepLines.length > 0 && (
              <StepLogs
                lines={stepLines}
                open={expandedSteps.has(step.key)}
                onToggle={() => toggleStep(step.key)}
              />
            )}
          </div>
        );
      })}
      {railwayFailed && project.railway_error_message && (
        <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-950/30">
          <p className="text-sm text-amber-700 dark:text-amber-300">
            Railway setup failed: {project.railway_error_message}
          </p>
        </div>
      )}
      {project.status === "failed" && project.error_message && (
        <div className="bg-destructive/10 border-destructive/30 mt-2 rounded-lg border p-3">
          <p className="text-destructive text-sm font-medium">{project.error_message}</p>
        </div>
      )}
      {project.status === "cancelled" && (
        <div className="border-border bg-secondary dark:border-border dark:bg-secondary/30 mt-2 rounded-lg border p-3">
          <p className="text-muted-foreground text-sm font-medium">Cancelled by user</p>
        </div>
      )}
      {!buildLog && (
        <p className="text-muted py-4 text-center text-xs">
          No detailed logs available for this project.
        </p>
      )}
    </div>
  );
}

/** Fetches and displays the AI-generated mascot (vibe message) for a completed project. */
function MascotContent({ projectId }: { projectId: number }) {
  const [vibe, setVibe] = useState<ProjectVibe | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [notReady, setNotReady] = useState(false);
  const [retryTick, setRetryTick] = useState(0);

  useEffect(() => {
    let cancelled = false;

    // Reset display state when projectId or retryTick changes. The effect
    // intentionally re-fetches whenever either dep changes.

    setLoading(true);

    setError(false);

    setNotReady(false);

    setVibe(null);

    getProjectVibe({ path: { project_id: projectId } })
      .then(({ data }) => {
        if (cancelled) {
          return;
        }
        setVibe(data ?? null);
      })
      .catch((err) => {
        if (cancelled) {
          return;
        }
        if (isApiError(err) && err.status === 404) {
          setNotReady(true);
          return;
        }
        logger.error("[project-detail] failed to fetch vibe message:", err);
        setError(true);
      })
      .finally(() => {
        if (cancelled) {
          return;
        }
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [projectId, retryTick]);

  if (loading) {
    return <p className="text-muted py-8 text-center text-sm">Loading mascot...</p>;
  }

  if (notReady || (!error && !vibe)) {
    return (
      <div className="flex flex-col items-center gap-3 py-8">
        <p className="text-muted text-center text-sm">
          Vibe message is still generating. Please try again in a moment.
        </p>
        <Button size="sm" variant="outline" onClick={() => setRetryTick((n) => n + 1)}>
          Retry
        </Button>
      </div>
    );
  }

  if (error || !vibe) {
    return (
      <div className="flex flex-col items-center gap-3 py-8">
        <p className="text-muted text-center text-sm">Failed to load mascot. Please retry.</p>
        <Button size="sm" variant="outline" onClick={() => setRetryTick((n) => n + 1)}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-4 py-2">
      {vibe.image_b64 && (
        <img
          src={`data:image/png;base64,${vibe.image_b64}`}
          alt={`${vibe.animal} mascot in ${vibe.style} style`}
          className="h-auto w-full max-w-xs rounded-xl shadow-md"
        />
      )}
      <div className="text-center">
        <h4 className="text-foreground text-lg font-bold">{vibe.heading}</h4>
        <p className="text-muted mt-1 max-w-md text-sm leading-relaxed">{vibe.body}</p>
      </div>
    </div>
  );
}

/** A single row in the detail view. */
function DetailRow({
  icon: Icon,
  label,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3 text-sm">
      <Icon className="text-muted size-4 shrink-0" />
      <span className="text-muted w-14 shrink-0">{label}</span>
      <span className="text-foreground flex items-center">{children}</span>
    </div>
  );
}
